import { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Plus,
  Search,
  Eye,
  Edit,
  Trash2,
  Download,
  FileText
} from 'lucide-react';
import { useShipping, useDeleteShipping, useGenerateShippingPDF } from '../../hooks/useShipping';
import { Button } from '../../components/ui/Button';
import { formatDate } from '../../lib/utils';
import { toast } from 'sonner';

export function ShippingList() {
  const [searchTerm, setSearchTerm] = useState('');
  const { data, isLoading, isError } = useShipping();
  const deleteShipping = useDeleteShipping();
  const generatePDF = useGenerateShippingPDF();

  const remitos = data?.data || [];
  const remitosFiltrados = remitos.filter((remito: any) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      remito.destinatarioNombre?.toLowerCase().includes(searchLower) ||
      remito.destinatarioDocumento?.toLowerCase().includes(searchLower) ||
      remito.destinatarioDireccion?.toLowerCase().includes(searchLower) ||
      remito.paqueteContenido?.toLowerCase().includes(searchLower)
    );
  });

  const handleDownloadPDF = async (id: number) => {
    try {
      const result = await generatePDF.mutateAsync(id);
      
      if (result.success) {
        toast.info('PDF generado, abriendo en nueva pestaña...');
        const pdfUrl = result.pdfUrl;
        window.open(pdfUrl, '_blank');
      }
    } catch (error: any) {
      // error ya manejado por react-query
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este remito?')) return;
    deleteShipping.mutate(id);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando remitos...</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-8 text-red-500">
        Error al cargar los remitos
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Remitos de Envío</h1>
          <p className="text-sm text-gray-500">
            {remitosFiltrados.length} remito(s) generados
          </p>
        </div>
        <Link to="/remitos/nuevo">
          <Button>
            <Plus size={18} />
            Nuevo Remito
          </Button>
        </Link>
      </div>

      {/* Búsqueda */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
        <input
          type="text"
          placeholder="Buscar por destinatario, documento o contenido..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        )}
      </div>

      {/* Lista de remitos */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {remitosFiltrados.map((remito: any) => (
          <div key={remito.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow">
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-semibold text-gray-800 truncate max-w-45">
                  {remito.destinatarioNombre}
                </h3>
                {remito.destinatarioDocumento && (
                  <p className="text-xs text-gray-400">Doc: {remito.destinatarioDocumento}</p>
                )}
              </div>
              <div className="flex gap-1">
                <Link
                  to={`/remitos/${remito.id}`}
                  className="p-1.5 text-blue-500 hover:bg-blue-50 rounded transition-colors"
                >
                  <Eye size={16} />
                </Link>
                <Link
                  to={`/remitos/editar/${remito.id}`}
                  className="p-1.5 text-gray-500 hover:bg-gray-50 rounded transition-colors"
                >
                  <Edit size={16} />
                </Link>
                <button
                  onClick={() => handleDelete(remito.id)}
                  className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                  title="Eliminar"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>

            {/* Detalles */}
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Contenido</span>
                <span className="text-gray-700 truncate max-w-37.5">
                  {remito.paqueteContenido}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Dirección</span>
                <span className="text-gray-600 truncate max-w-37.5 text-xs">
                  {remito.destinatarioDireccion}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Fecha</span>
                <span className="text-xs text-gray-400">
                  {formatDate(remito.fechaGeneracion)}
                </span>
              </div>
              {remito.notasAdicionales && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Notas</span>
                  <span className="text-xs text-gray-500 truncate max-w-37.5">
                    {remito.notasAdicionales}
                  </span>
                </div>
              )}
            </div>

            {/* Acciones */}
            <div className="mt-3 pt-3 border-t border-gray-100 flex gap-2">
              <button
                onClick={() => handleDownloadPDF(remito.id)}
                className="flex-1 flex items-center justify-center gap-1 text-sm text-gray-700 border border-gray-300 hover:bg-gray-50 py-1.5 rounded-lg transition-colors"
              >
                <Download size={14} />
                {remito.pdfUrl ? 'Ver PDF' : 'Generar PDF'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {remitosFiltrados.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <FileText size={48} className="mx-auto mb-4 text-gray-300" />
          <p>No se encontraron remitos</p>
          {searchTerm && (
            <p className="text-sm mt-1">
              No hay remitos que coincidan con "{searchTerm}"
            </p>
          )}
          <Link to="/remitos/nuevo" className="text-blue-600 hover:underline text-sm mt-2 inline-block">
            Crear el primer remito
          </Link>
        </div>
      )}
    </div>
  );
}